import { isMobile } from "react-device-detect"
import {
  DEFAULT_TIRE_QUENTY,
  END_PRICE,
  FILTER_NAME_FORMAT,
  PRICE_SORT,
  START_PRICE,
  TIRE_ADDED,
  TIRE_FILTER_CHECKBOX,
  TIRE_FILTER_RANGE,
  TIRE_FILTER_SORTING,
  TIRE_KEY
} from "../../v1/constants/tires"
import { isEmpty } from "lodash"
import store from "./configureStore"

/**
 * Transforms a given string into a capitalized format.
 *
 * @param {string} input - The input string to transform.
 * @returns {string} - The transformed string.
 *
 * @example
 * // Returns 'Brand'
 * transformString('brand');
 *
 * @example
 * // Returns 'Hello World'
 * transformString('hello_world');
 */
export const transformString = (input) => {
  return input
    .split(FILTER_NAME_FORMAT) // Split the string by hyphens and underscores
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize the first letter of each word
    .join(" ") // Join the words with a space
}

/**
 * Converts selected filters to a query string for use in a URL.
 *
 * @param {Object} filters - An object representing selected filters.
 * @returns {string} - A URL string with the query parameters.
 *
 * Example of the filters object:
 * {
 *   speed_rating: { checkbox: ['V', 'H'] },
 *   load_index: { checkbox: [1, 2] },
 *   price: { range: { min: 100, max: 500 } },
 *   sort: { sorting: 'desc' }
 * }
 */
export const prepareFilterAPIUrl = (filters) => {
  const queryParams = []

  // Iterate over each filter type and its values
  for (const filterType in filters) {
    if (filters.hasOwnProperty(filterType)) {
      const filterValues = filters[filterType]

      if (filterValues.checkbox && filterValues.checkbox.length > 0) {
        // Add each checkbox value as a separate query parameter
        filterValues.checkbox.forEach((value) => {
          queryParams.push(`${encodeURIComponent(filterType)}[]=${encodeURIComponent(value)}`)
        })
      }

      if (filterValues.range) {
        if (filterValues.range.start_price !== undefined) {
          queryParams.push(`${START_PRICE}=${encodeURIComponent(filterValues.range.start_price)}`)
        }
        if (filterValues.range.end_price !== undefined) {
          queryParams.push(`${END_PRICE}=${encodeURIComponent(filterValues.range.end_price)}`)
        }
      }

      if (filterValues.sorting) {
        queryParams.push(`${PRICE_SORT}=${encodeURIComponent(filterValues.sorting)}`)
      }
    }
  }

  return queryParams.join("&")
}

/**
 * Manages tire filter state updates and triggers data fetching.
 *
 * This function updates the `tireSelectedFilters` state based on the selected filter's type and category,
 * and optionally triggers an API call to fetch filtered data. It supports checkbox, range, and sorting filters.
 *
 * @param {Object} params - Parameters for managing the tire filters.
 * @param {Object} params.selectedFilter - The filter data selected by the user.
 * @param {string} params.filterType - The type of filter being applied (e.g., 'speed_rating', 'load_index').
 * @param {string} params.filterCategory - The category of the filter (e.g., 'TIRE_FILTER_CHECKBOX', 'TIRE_FILTER_RANGE', 'TIRE_FILTER_SORTING').
 * @param {Function} params.setTireSelectedFilters - Function to update the state of selected filters.
 * @param {Function} params.fetchTireData - Function to fetch tire data based on the updated filters.
 *
 * @returns {Object} The updated filter state.
 */
export const manageTireFilters = ({
  selectedFilter,
  filterType,
  filterCategory,
  setTireSelectedFilters,
  fetchTireData
}) => {
  setTireSelectedFilters((prevFilters) => {
    const currentFilters = prevFilters[filterType] || []
    let updatedFilters

    switch (filterCategory) {
      case TIRE_FILTER_CHECKBOX:
        const checkboxFilters = currentFilters.checkbox || []
        if (selectedFilter.checked) {
          updatedFilters = {
            ...prevFilters,
            [filterType]: {
              ...currentFilters,
              checkbox: [...checkboxFilters, selectedFilter.label]
            }
          }
        } else {
          updatedFilters = {
            ...prevFilters,
            [filterType]: {
              ...currentFilters,
              checkbox: checkboxFilters.filter((label) => label !== selectedFilter.label)
            }
          }
        }
        break

      case TIRE_FILTER_RANGE:
        updatedFilters = {
          ...prevFilters,
          [filterType]: {
            ...currentFilters,
            range: {
              start_price: selectedFilter.min,
              end_price: selectedFilter.max
            }
          }
        }
        break

      case TIRE_FILTER_SORTING:
        updatedFilters = {
          ...prevFilters,
          [filterType]: {
            ...currentFilters,
            sorting: selectedFilter.value
          }
        }
        break

      default:
        updatedFilters = prevFilters
        break
    }

    // Trigger API call immediately if on desktop
    if (!isMobile) {
      fetchTireData(updatedFilters)
    }

    return updatedFilters
  })
}

/**
 * Checks if all filters are empty.
 * @param {Object} filters - The filters object to check.
 * @returns {boolean} - Returns true if all filters are empty, false otherwise.
 */
export const areTireFiltersEmpty = (filters) => {
  return !Object.values(filters).some(
    (filter) =>
      (filter.checkbox && filter.checkbox.length > 0) ||
      (filter.range && (filter.range.start_price || filter.range.end_price)) ||
      (filter.sorting && filter.sorting)
  )
}

export const shouldApplyFilterRendered = (selectedFilters) => {
  return Object.values(selectedFilters).some(
    (filter) =>
      (filter.checkbox && filter.checkbox.length > 0) ||
      (filter.range && (filter.range.start_price || filter.range.end_price))
    //  ||  (filter.sorting && filter.sorting) If want to consider sorting filter as an part of applied filter then uncomment this line
  )
}

export const getKlaviyoTireInfo = ({
  tire,
  t,
  quantity = DEFAULT_TIRE_QUENTY,
  action = TIRE_ADDED,
  deeplink = window.location.href
}) => {
  return {
    tireBrand: tire.brand,
    tireDetails: tire.size,
    tireImage: tire.image,
    tirePrice: tire.cost,
    tireRetailPrice: tire.price,
    tireQuantity: quantity,
    tireTotal: quantity * tire.cost,
    tireRatings:
      !isEmpty(tire.external_info) && !isEmpty(tire.external_info.rating)
        ? tire.external_info.rating.average_rating || t("common:NA")
        : t("common:NA"),
    action,
    deeplink
  }
}

export const getTcTireSearchKlaviyoInfo = ({
  activeVehicle,
  tiresList,
  factoryTireSize,
  tireSelectedFilters,
  deeplink = window.location.href
}) => {
  return {
    vehicleYear: activeVehicle.year,
    vehicleMake: activeVehicle.make,
    vehicleModel: activeVehicle.model,
    factoryTireSize: factoryTireSize,
    availableTiresCount: tiresList.length,
    tireSelectedFilters: JSON.stringify(tireSelectedFilters),
    deeplink
  }
}

/**
 * Builds a query URL based on the provided object and adds it to the current URL.
 * Optionally includes a user authentication token if the flag is set to true.
 *
 * @param {Object} queryParams - An object containing key-value pairs for query parameters.
 * @param {boolean} [includeAuth=false] - A flag to indicate if the user's authentication token should be included.
 * @returns {string} - The constructed URL with query parameters appended.
 */
export const buildQueryUrl = (queryParams, includeAuth = false, routePath = "#/top-tires") => {
  const baseUrl = window.location.origin // Gets the base URL without the hash and query params
  const hashPath = routePath
  const urlParams = new URLSearchParams()
  const user = store.getState().user // Get the user object from Redux

  // Loop through the queryParams object and add each key-value pair as query parameters
  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key]) {
      urlParams.append(key, queryParams[key])
    }
  })

  // If the flag is true, include the authentication token from the user object
  if (includeAuth && user && user.authentication_token) {
    urlParams.append(TIRE_KEY, user.authentication_token)
  }

  // Construct the final URL
  return `${baseUrl}/${hashPath}?${urlParams.toString()}`
}
